// Self-hosted PhotoSwipe lightbox over .gh-content images (D-05/D-06).
// Progressive enhancement: with JS off, images still render as plain content.
// Source: https://photoswipe.com/getting-started/  (PhotoSwipe 5.4.4)
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";
import "photoswipe/style.css";

// Ghost emits <img> (not anchors), but PhotoSwipe v5 opens an <a> and REQUIRES
// data-pswp-width / data-pswp-height on it. So we wrap the content images at
// runtime, sourcing dimensions from the width/height attrs (available pre-load)
// with naturalWidth/Height as the fallback for attribute-less placeholder uploads.
export default function lightbox() {
    const content = document.querySelector(".gh-content");
    if (!content) {
        return;
    } // progressive-enhancement guard

    // D-06 scope: standalone content images + gallery images; skip inline icons.
    const imgs = content.querySelectorAll(
        ".kg-image-card img, .kg-gallery-image img"
    );
    if (!imgs.length) {
        return;
    }

    imgs.forEach((img) => {
        if (img.closest("a[data-pswp-width]")) {
            return;
        } // idempotent

        const a = document.createElement("a");
        // strip Ghost's /size/wXXX/ segment to open the original
        a.href = (img.currentSrc || img.src).replace(/\/size\/[^/]+\//, "/");
        a.dataset.pswpWidth = img.getAttribute("width") || img.naturalWidth || 0;
        a.dataset.pswpHeight =
            img.getAttribute("height") || img.naturalHeight || 0;

        // Caption source (priority: img alt → nearest card figcaption → none).
        // Captured here, while the img is still in its original DOM context, and
        // stashed on the anchor so the caption uiElement can read it at slide time.
        // For a gallery image the nearest figcaption is the gallery card's single
        // shared <figcaption>; for a standalone .kg-image-card it's that card's.
        let caption = (img.getAttribute("alt") || "").trim();
        if (!caption) {
            const card = img.closest(".kg-gallery-card, .kg-image-card");
            const figcap = card && card.querySelector("figcaption");
            if (figcap) {
                caption = figcap.textContent.trim();
            }
        }
        if (caption) {
            a.dataset.pswpCaption = caption;
        }

        a.appendChild(img.cloneNode());
        img.replaceWith(a);
    });

    const pswp = new PhotoSwipeLightbox({
        gallery: ".gh-content",
        children: "a[data-pswp-width]",
        // Static module ref (not a dynamic import): the theme bundles to a single
        // IIFE which cannot code-split, so PhotoSwipe core is bundled in — still
        // fully self-hosted (no CDN), no rollup.config.js change.
        pswpModule: PhotoSwipe,
    });

    // PhotoSwipe v5 ships no caption by default; register a custom caption
    // uiElement (the documented no-plugin approach).
    // Source: https://photoswipe.com/caption/  (uiRegister + registerElement).
    pswp.on("uiRegister", () => {
        pswp.pswp.ui.registerElement({
            name: "custom-caption", // → element class .pswp__custom-caption
            order: 9,
            isButton: false,
            appendTo: "root",
            onInit: (el, pswpInstance) => {
                pswpInstance.on("change", () => {
                    const anchor = pswpInstance.currSlide?.data?.element;
                    const text = (anchor && anchor.dataset.pswpCaption) || "";
                    // textContent (never innerHTML): caption is author text kept
                    // as a plain string — no raw HTML reaches the DOM (T-04.2-01).
                    el.textContent = text;
                    // Hide the bar entirely when there's no caption (no stray pill).
                    el.style.display = text ? "" : "none";
                });
            },
        });
    });

    pswp.init();
}
